import React, { useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Switch,
  Text,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import TwoFAMethodSelector from "../TwoFAMethodSelector";
import CurrencySelector from "../CurrencySelector";

const SwitchRow = ({ label, isChecked, onChange, isDisabled }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" w="100%">
    <Text fontSize="sm">{label}</Text>
    <Switch isChecked={isChecked} onChange={onChange} isDisabled={isDisabled} />
  </Box>
);

const SettingsDialog = ({
  isOpen,
  onClose,
  user,
  preferences,
  hasPreferences,
  togglingFlag,
  twofamethodsString,
  currencies,
  onToggleAvTracking,
  onToggleEmailOptin,
  onTogglePublicProfile,
  onToggle2FA,
  onToggleTwoFAMethod,
  onToggleCurrencyCode,
}) => {
  const cancelRef = useRef();
  const { colorMode, toggleColorMode } = useColorMode();

  return (
    <AlertDialog
      motionPreset="slideInBottom"
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered={true}
      closeOnOverlayClick={true}
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          maxWidth="350px"
          minWidth="300px"
          overflowX="auto"
          minHeight="250px"
        >
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Settings
          </AlertDialogHeader>
          <AlertDialogBody
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <VStack spacing={4} w="100%" align="stretch">
              <SwitchRow
                label={`${colorMode === "light" ? "Light" : "Dark"} Mode`}
                isChecked={colorMode === "dark"}
                onChange={toggleColorMode}
              />
              <SwitchRow
                label="Balance Tracking"
                isChecked={hasPreferences && preferences.account_value_optin === 1}
                onChange={onToggleAvTracking}
                isDisabled={togglingFlag === "account_value_optin"}
              />
              <SwitchRow
                label="Email Alerts"
                isChecked={hasPreferences && preferences.email_optin === 1}
                onChange={onToggleEmailOptin}
                isDisabled={togglingFlag === "email_optin"}
              />
              <SwitchRow
                label="Public Profile"
                isChecked={hasPreferences && preferences["public_profile_optin"] === 1}
                onChange={onTogglePublicProfile}
                isDisabled={togglingFlag === "public_profile_optin"}
              />

              <FormControl isDisabled={!user.has_password}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  w="100%"
                >
                  <Text fontSize="sm">Two-Factor Auth</Text>
                  <Switch
                    isChecked={
                      hasPreferences && preferences["2fa_optin"] === 1
                    }
                    onChange={onToggle2FA}
                    isDisabled={
                      !user.has_password || togglingFlag === "2fa_optin"
                    }
                  />
                </Box>
                <FormHelperText fontSize="xs" mt={1}>
                  {!user.has_password
                    ? "Two-factor authentication is not available for Google sign-in accounts. Your Google account's own security settings apply."
                    : "Two-factor authentication only applies to password sign-in. Sign-in via Google is secured by your Google account's own 2FA settings."}
                </FormHelperText>
              </FormControl>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                w="100%"
                opacity={!user.has_password ? 0.4 : 1}
              >
                <Text fontSize="sm" flexShrink={0}>2FA Method</Text>
                <Box w="140px">
                  <TwoFAMethodSelector
                    isDisabled={
                      (hasPreferences && preferences["2fa_optin"] === 0) ||
                      !user.has_password ||
                      togglingFlag === "preferred_2fa_method"
                    }
                    methods={twofamethodsString}
                    preferences={preferences}
                    handleToggleTwoFAMethod={onToggleTwoFAMethod}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                w="100%"
              >
                <Text fontSize="sm" flexShrink={0}>Currency</Text>
                <Box w="140px">
                  <CurrencySelector
                    currencies={currencies}
                    preferences={preferences}
                    handleToggleCurrencyCode={onToggleCurrencyCode}
                    isDisabled={togglingFlag === "preferred_currency"}
                  />
                </Box>
              </Box>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Done
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default SettingsDialog;
